import { Add } from '@mui/icons-material';
import { Autocomplete, Box, Button, TextField, Typography } from '@mui/material';
import type { Ref } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../../hooks/use-is-mobile';
import type { Supplier } from '../../../services/goods-receiving';
import { useSuppliers } from '../hooks/use-suppliers';
import { SupplierFormDialog } from './supplier-form-dialog';

interface SupplierAutocompleteProps {
  value: Supplier | null;
  onChange: (supplier: Supplier | null) => void;
  error?: boolean;
  helperText?: string;
  inputRef?: Ref<HTMLInputElement>;
}

export function SupplierAutocomplete({
  value,
  onChange,
  error = false,
  helperText,
  inputRef,
}: SupplierAutocompleteProps) {
  const { t } = useTranslation('goodsReceiving');
  const isMobile = useIsMobile();
  const { data: suppliers = [], isLoading } = useSuppliers();
  const [formOpen, setFormOpen] = useState(false);

  function handleSupplierCreated(supplier: { id: string; name: string }) {
    onChange(supplier as Supplier);
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Autocomplete
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        options={suppliers}
        loading={isLoading}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            label={t('supplierLabel')}
            placeholder={t('supplierPlaceholder')}
            error={error}
            helperText={helperText}
            required
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps?.input,
                sx: { fontSize: isMobile ? '16px' : undefined },
              },
            }}
          />
        )}
        renderOption={({ key, ...props }, option) => (
          <Box component="li" key={key} {...props}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Typography variant="body1">{option.name}</Typography>
              {option.vatNumber && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {t('vatNumberPrefix')} {option.vatNumber}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        fullWidth
        noOptionsText={
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('noSupplierFound')}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Add />}
              onClick={() => setFormOpen(true)}
            >
              {t('createNewSupplier')}
            </Button>
          </Box>
        }
      />
      <Button
        variant="outlined"
        startIcon={<Add />}
        onClick={() => setFormOpen(true)}
        fullWidth={isMobile}
      >
        {t('newSupplier')}
      </Button>
      <SupplierFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onCreated={handleSupplierCreated}
      />
    </Box>
  );
}
